import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ScrollToTop } from '@/components/ScrollToTop';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Suspense, lazy } from 'react';

// Loading fallback component - minimal for fast FCP
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Critical path - load immediately (homepage)
import HomePage from '@/pages/HomePage';

// Lazy load all other pages for better TBT and TTI
const ServicePage = lazy(() => import('@/pages/ServicePage'));
const KnowledgeHubPage = lazy(() => import('@/pages/KnowledgeHubPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const AboutAsbestosPage = lazy(() => import('@/pages/AboutAsbestosPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const ComplaintsPage = lazy(() => import('@/pages/ComplaintsPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('@/pages/TermsConditionsPage'));
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'));
const QualityPolicyPage = lazy(() => import('@/pages/QualityPolicyPage'));
const QMSScopePage = lazy(() => import('@/pages/QMSScopePage'));
const ComplianceCommitmentPage = lazy(() => import('@/pages/ComplianceCommitmentPage'));
const LocationPage = lazy(() => import('@/pages/LocationPage'));
const CaseStudiesPage = lazy(() => import('@/pages/CaseStudiesPage'));
const GuidesPage = lazy(() => import('@/pages/GuidesPage'));
const GuidePage = lazy(() => import('@/pages/GuidePage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));

// CRM pages - lazy loaded (not critical for public users)
const LoginPage = lazy(() => import('@/pages/crm/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/crm/DashboardPage'));
const OrganizationsPage = lazy(() => import('@/pages/crm/OrganizationsPage'));
const OrganizationFormPage = lazy(() => import('@/pages/crm/OrganizationFormPage'));
const OrganizationDetailPage = lazy(() => import('@/pages/crm/OrganizationDetailPage'));
const EnquiriesPage = lazy(() => import('@/pages/crm/EnquiriesPage'));
const EnquiryDetailPage = lazy(() => import('@/pages/crm/EnquiryDetailPage'));
const ContactsPage = lazy(() => import('@/pages/crm/ContactsPage'));
const ProjectsPage = lazy(() => import('@/pages/crm/ProjectsPage'));
const ProjectFormPage = lazy(() => import('@/pages/crm/ProjectFormPage'));
const ProjectDetailPage = lazy(() => import('@/pages/crm/ProjectDetailPage'));
const QuotesPage = lazy(() => import('@/pages/crm/QuotesPage'));
const QuoteDetailPage = lazy(() => import('@/pages/crm/QuoteDetailPage'));
const QuoteFormPage = lazy(() => import('@/pages/crm/QuoteFormPage'));
const ACMRegisterPage = lazy(() => import('@/pages/crm/ACMRegisterPage'));
const SiteDetailPage = lazy(() => import('@/pages/crm/SiteDetailPage'));
const UserManagementPage = lazy(() => import('@/pages/crm/UserManagementPage'));
const ChangePasswordPage = lazy(() => import('@/pages/crm/ChangePasswordPage'));
const StaffCompetencePage = lazy(() => import('@/pages/crm/StaffCompetencePage'));
const StaffProfilePage = lazy(() => import('@/pages/crm/StaffProfilePage'));
const StaffFormPage = lazy(() => import('@/pages/crm/StaffFormPage'));
const TrainingMatrixPage = lazy(() => import('@/pages/crm/TrainingMatrixPage'));
const BulkImportPage = lazy(() => import('@/pages/crm/BulkImportPage'));
const SchedulingPage = lazy(() => import('@/pages/crm/SchedulingPage'));

// Client Portal pages - lazy loaded
const ClientPortalLogin = lazy(() => import('@/pages/portal/ClientPortalLogin'));
const ClientPortalDashboard = lazy(() => import('@/pages/portal/ClientPortalDashboard'));
const ClientPropertiesPage = lazy(() => import('@/pages/portal/ClientPropertiesPage'));
const ClientPropertyDetailPage = lazy(() => import('@/pages/portal/ClientPropertyDetailPage'));
const ClientDocumentsPage = lazy(() => import('@/pages/portal/ClientDocumentsPage'));
const ClientACMRegisterPage = lazy(() => import('@/pages/portal/ClientACMRegisterPage'));
const ClientBuildingACMPage = lazy(() => import('@/pages/portal/ClientBuildingACMPage'));

// Lead gen components - lazy loaded (below fold)
import { ExitIntentPopup, FloatingQuoteBar, StickyCallBar } from '@/components/LeadGenCTAs';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/crm/login" replace />;
  }
  
  return children;
};

// Public layout wrapper
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

// CRM layout wrapper (no public header/footer)
const CRMLayout = ({ children }) => (
  <main className="min-h-screen bg-gray-50">{children}</main>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster position="top-right" />
          
          {/* Lead gen components loaded after main content */}
          <ExitIntentPopup />
          <FloatingQuoteBar />
          <StickyCallBar />
          
          <Routes>
            {/* Homepage - not lazy loaded for best FCP/LCP */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            
            {/* Public routes - lazy loaded */}
            <Route path="/services/:slug" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><ServicePage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/knowledge-hub" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><KnowledgeHubPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/knowledge-hub/:slug" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><BlogPostPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><ContactPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/about-asbestos" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><AboutAsbestosPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/faq" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><FAQPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/complaints" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><ComplaintsPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/privacy-policy" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><PrivacyPolicyPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/terms-conditions" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><TermsConditionsPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/terms-of-service" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><TermsOfServicePage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/quality-policy" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><QualityPolicyPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/qms-scope" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><QMSScopePage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/compliance-commitment" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><ComplianceCommitmentPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/locations/:slug" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><LocationPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/case-studies" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><CaseStudiesPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/guides" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><GuidesPage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/guides/:slug" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><GuidePage /></PublicLayout>
              </Suspense>
            } />
            <Route path="/team" element={
              <Suspense fallback={<PublicLayout><PageLoader /></PublicLayout>}>
                <PublicLayout><TeamPage /></PublicLayout>
              </Suspense>
            } />
            
            {/* CRM Routes */}
            <Route path="/crm/login" element={
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            } />
            <Route path="/crm/dashboard" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><DashboardPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/organizations" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><OrganizationsPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/organizations/new" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><OrganizationFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/organizations/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><OrganizationDetailPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/organizations/:id/edit" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><OrganizationFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/enquiries" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><EnquiriesPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/enquiries/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><EnquiryDetailPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/contacts" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ContactsPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/projects" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ProjectsPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/projects/new" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ProjectFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/projects/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ProjectDetailPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/projects/:id/edit" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ProjectFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/quotes" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><QuotesPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/quotes/new" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><QuoteFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/quotes/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><QuoteDetailPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/quotes/:id/edit" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><QuoteFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/acm-register" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ACMRegisterPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/sites/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><SiteDetailPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/users" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><UserManagementPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/change-password" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><ChangePasswordPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/staff-competence" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><StaffCompetencePage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/staff-competence/new" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><StaffFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/staff-competence/:id" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><StaffProfilePage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/staff-competence/:id/edit" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><StaffFormPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/training-matrix" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><TrainingMatrixPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/bulk-import" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><BulkImportPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            <Route path="/crm/scheduling" element={
              <Suspense fallback={<CRMLayout><PageLoader /></CRMLayout>}>
                <ProtectedRoute><CRMLayout><SchedulingPage /></CRMLayout></ProtectedRoute>
              </Suspense>
            } />
            
            {/* Client Portal Routes */}
            <Route path="/portal/login" element={
              <Suspense fallback={<PageLoader />}>
                <ClientPortalLogin />
              </Suspense>
            } />
            <Route path="/portal/dashboard" element={
              <Suspense fallback={<PageLoader />}>
                <ClientPortalDashboard />
              </Suspense>
            } />
            <Route path="/portal/properties" element={
              <Suspense fallback={<PageLoader />}>
                <ClientPropertiesPage />
              </Suspense>
            } />
            <Route path="/portal/properties/:id" element={
              <Suspense fallback={<PageLoader />}>
                <ClientPropertyDetailPage />
              </Suspense>
            } />
            <Route path="/portal/documents" element={
              <Suspense fallback={<PageLoader />}>
                <ClientDocumentsPage />
              </Suspense>
            } />
            <Route path="/portal/acm-register" element={
              <Suspense fallback={<PageLoader />}>
                <ClientACMRegisterPage />
              </Suspense>
            } />
            <Route path="/portal/acm-register/:buildingId" element={
              <Suspense fallback={<PageLoader />}>
                <ClientBuildingACMPage />
              </Suspense>
            } />
            
            {/* Catch-all redirect to homepage */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
